import React, { useMemo } from 'react';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useQuery } from '@apollo/client';
import ProductUsageYearlyOverviewQuery from '../../graphql/queries/product/ProductUsageYearlyOverview.graphql';
import normalizeVariables, { normalizeFields } from '../../graphql/schema/normalizeVariables';
import { IFormatterProps } from '../../@types/workspaceTypes';
import { ResponsiveLine } from '@nivo/line';
import {
  IProduct,
  IProductUsageYearlyOverviewQueryResult,
  IProductUsageYearlyOverviewQueryVariables,
  IWarehouse,
  Maybe,
} from '../../graphql/schema/p4types';
import EditableDataGrid from '../../components/EditableDataGrid';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const ProductWarehouseUsageGrid = ({
  warehouse,
  product,
}: {
  warehouse: Maybe<IWarehouse>;
  product: Maybe<IProduct>;
}) => {
  const handleQueryError = useComponentQueryErrorHandling();

  const { data } = useQuery<IProductUsageYearlyOverviewQueryResult, IProductUsageYearlyOverviewQueryVariables>(
    ProductUsageYearlyOverviewQuery,
    {
      onError: handleQueryError,
      variables: {
        productUsageOverviewRequest: {
          warehouse: normalizeVariables(warehouse),
          product: normalizeVariables(product, [...normalizeFields, 'alternativeSellingUnits']),
        },
      },
      fetchPolicy: 'cache-and-network',
      skip: !warehouse || !product,
    }
  );

  const productUsageColumns = useMemo(() => {
    let dateRangeColumns: Array<IEditableDataGridColumnDef> = [];

    if (data?.productUsageYearlyOverview?.periodUsages) {
      dateRangeColumns = data.productUsageYearlyOverview.periodUsages.map((periodUsage) => ({
        name: periodUsage?.periodShortName,
        key: periodUsage?.periodShortName,
        editable: false,
      }));
    }

    return [
      ...dateRangeColumns,
      {
        name: 'Trend',
        key: 'chart',
        editable: false,
        width: 140,
        formatter: ({
          value,
        }: IFormatterProps<
          {
            averagePerPeriod: number;
            freeStock: number;
            levelStock: number;
            nextMonthSales: number;
            chart: { x: number; y: number }[];
          } & { [key: string]: number | { x: number; y: number }[] },
          Maybe<{ x: number; y: number }[]>
        >) => {
          return (
            <div style={{ width: 120, height: 20 }}>
              <ResponsiveLine
                data={[{ id: 'spark', data: value ?? [] }]}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                xScale={{ type: 'linear' }}
                yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                axisTop={null}
                axisRight={null}
                axisBottom={null}
                axisLeft={null}
                enableGridX={false}
                enableGridY={false}
                enablePoints={false}
                lineWidth={2}
                colors={['#1f77b4']}
                isInteractive={false}
              />
            </div>
          );
        },
      },
      {
        name: 'Next 30 Days Sales',
        key: 'nextMonthSales',
        editable: false,
        width: 140,
      },
      {
        name: 'Average Per Period',
        key: 'averagePerPeriod',
        editable: false,
        width: 140,
      },
      {
        name: 'Available Free',
        key: 'freeStock',
        editable: false,
        width: 140,
      },
      {
        name: 'Available Level',
        key: 'levelStock',
        editable: false,
        width: 140,
      },
    ];
  }, [data?.productUsageYearlyOverview?.periodUsages]);

  const listData = useMemo(() => {
    const { periodUsages, nextMonthSales, averagePerPeriod, freeStock, levelStock } =
      data?.productUsageYearlyOverview || {};
    const lineChartValues: { x: number; y: number }[] = [];

    const dateKeys = periodUsages?.map((periodUsage) => periodUsage?.periodShortName);

    if (dateKeys) {
      let i = 0;
      dateKeys.forEach((dateKey) => {
        lineChartValues.push({
          x: i,
          y: periodUsages?.find((periodUsage) => periodUsage?.periodShortName === dateKey)?.usage ?? 0,
        });

        i++;
      });
    }

    const dateUsageMap = periodUsages?.reduce((acc, periodUsage) => {
      if (periodUsage?.periodShortName) {
        acc[periodUsage.periodShortName] = periodUsage.usage;
      }
      return acc;
    }, {} as { [key: string]: number });

    return [
      {
        nextMonthSales,
        averagePerPeriod,
        freeStock,
        levelStock,
        chart: lineChartValues,
        ...dateUsageMap,
      },
    ];
  }, [data?.productUsageYearlyOverview]);

  // Defer rendering until data is loaded, so the grid doesn't appear "glitchy" during load.
  if (!warehouse || !product || !data) {
    return null;
  }

  return (
    <EditableDataGrid
      columns={productUsageColumns}
      addBlankRow={false}
      enableCellSelect={true}
      enableCellAutoFocus={false}
      listData={listData}
      rowsCount={1}
      contentHeight={90}
      contextKey={'shortage-product-usage'}
    />
  );
};

export default ProductWarehouseUsageGrid;
